<template>

  <!-- If the user is NOT logged in, just show the brand image and login button -->
  <nav v-if="!isLoggedIn" class="navbar fixed-top bg-body-tertiary">
    <!--brand-->
    <router-link to="/dashboard" @click="collapseOpenMenu()" class="navbar-brand mr-auto">
      <LogoWithText :height="45"></LogoWithText>
    </router-link>

    <!--login-->
    <router-link
        class="btn btn-primary btn-login"
        :to="{ name: defaultCreateAccount? 'SignUpPage' : 'LogInPage'}"
        v-text="defaultCreateAccount? 'sign-up / log-in' : 'log in'">
    </router-link>
  </nav>


  <!-- If the user IS logged in, show everything -->
  <nav v-else class="navbar fixed-top navbar-expand-lg bg-body-tertiary">

    <!--brand-->
    <router-link to="/dashboard" @click="collapseOpenMenu()" class="navbar-brand mr-auto">
      <LogoWithText :height="45"></LogoWithText>
    </router-link>

    <!--user-->
    <div class="nav-item dropdown-center user-dropdown text-center text-lg-start">
      <a class="nav-link" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
        <ProfilePicture :user="user" :size="20"></ProfilePicture> <ProfileName :user="user"></ProfileName>
      </a>
      <ul class="dropdown-menu">
        <li><router-link @click="collapseOpenMenu()" class="dropdown-item" :to="`/profile/${user._id}`"><i class="fa fa-user-circle"></i> profile</router-link></li>
        <li><router-link @click="collapseOpenMenu()" class="dropdown-item" to="/settings"><i class="fa fa-cog"></i> settings</router-link></li>
        <li><router-link @click="collapseOpenMenu()" class="dropdown-item" to="/membership">
          <ColorThemeImg :src="`${$imagePathBase}logo.svg`" :dark_theme_src="`${$imagePathBase}logo-white.svg`" :height="16"/> membership</router-link>
        </li>
        <li v-if="isAdmin"><router-link @click="collapseOpenMenu()" class="dropdown-item admin-colors" to="/admin"><i class="fa fa-wrench"></i> admin</router-link></li>
        <li><hr class="dropdown-divider"></li>
        <li><a class="dropdown-item" @click="$emit('log-out-user')" href="#"><i class="fa fa-sign-out"></i> log out</a></li>
      </ul>
    </div>


    <!--menu-->
    <button class="navbar-toggler collapsed" ref="navtoggle" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
      <span class="navbar-toggler-icon">
        <span v-if="navbarTogglerPillNumber > 0" class="badge rounded-pill bg-danger">{{navbarTogglerPillNumber}}</span>
      </span>
    </button>

    <!--nav links-->
    <div class="collapse navbar-collapse mx-auto" id="navbarSupportedContent">
      <ul class="navbar-nav mx-auto mb-2 mb-lg-0">
        <li class="nav-item">
          <router-link to="/dashboard" @click="collapseOpenMenu()" :class="{ active: isActive('dashboard'), disabled: !isLoggedIn }" class="nav-link">Dashboard</router-link>
        </li>
        <li class="nav-item">
          <router-link to="/checklist" @click="collapseOpenMenu()" :class="{ active: isActive('checklist'), disabled: !isLoggedIn }" class="nav-link">Checklists</router-link>
        </li>
        <li class="nav-item">
          <router-link to="/viewing-options" @click="collapseOpenMenu()" :class="{ active: isActive('viewing-options'), disabled: !isLoggedIn }" class="nav-link">Viewing Options</router-link>
        </li>
        <li class="nav-item">
          <router-link to="/friends" @click="collapseOpenMenu()" :class="{ active: isActive('friends'), disabled: !isLoggedIn }" class="nav-link">
            Friends<span v-if="friendInvitePillNumber > 0" class="badge rounded-pill bg-danger">{{friendInvitePillNumber}}</span>
          </router-link>
        </li>
        <li v-if="showPoolsTab" class="nav-item">
          <router-link to="/oscar-pools" @click="collapseOpenMenu()" :class="{ active: isActive('clubs'), disabled: !isLoggedIn }" class="nav-link">
            Pools<span v-if="pendingClubActionCount > 0" class="badge rounded-pill bg-danger">{{pendingClubActionCount}}</span>
          </router-link>
        </li>
      </ul>

      <!--search-->
      <div class="nav-item">
        <form class="form-search">
          <div class="input-group">

            <!-- display disabled one if user is not logged in-->
            <input class="form-control" type="text" placeholder="search" v-model="searchText" />

            <div class="btn-group search-button">
              <input class="btn btn-outline-secondary btn-sm" :class="{disabled: !isLoggedIn}" type="submit" :value="`${searchType}`" @click.prevent="onSearch"/>
              <button type="button" class="btn btn-outline-secondary dropdown-toggle btn-sm dropdown-toggle-split" :class="{disabled: !isLoggedIn}" data-bs-toggle="dropdown" aria-expanded="false">
                <span class="visually-hidden">Toggle Dropdown</span>
              </button>

              <ul class="dropdown-menu search-dropdown dropdown-menu-end">
                <li><a class="dropdown-item" href="#" @click.prevent="this.searchType = 'Titles'">Film Titles</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="this.searchType = 'Overviews'">Film Overviews</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="this.searchType = 'Nominees'">Nominees</a></li>
              </ul>
            </div>
          </div>
        </form>
      </div>

    </div> <!--navbar collapse-->
  </nav>
</template>


<script>
import {userStore} from "@/userStore";
import {EventBus} from "@/eventBus";
import ProfilePicture from "@/components/profile/ProfilePicture.vue";
import ProfileName from "@/components/profile/ProfileName.vue";
import LogoWithText from "@/components/LogoWithText.vue";
import ColorThemeImg from "@/components/ColorThemeImg.vue";

export default {
  name: "NavBar",
  components: {ColorThemeImg, LogoWithText, ProfileName, ProfilePicture},
  computed: {
    defaultCreateAccount() {
      return (this.$route.query['new-acct'] === 'true' ||
          Object.hasOwn(this.$route.query, 'utm_source') || Object.hasOwn(this.$route.query, 'utm-source') ||
          Object.hasOwn(this.$route.query, 'fbclid'));
    },

    isLoggedIn() {
      return userStore.isLoggedIn();
    },
    isFullMember() {
      return userStore.isFullMember();
    },
    isAdmin() {
      return userStore.isAdmin();
    },
    isTester() {
      return userStore.isTester();
    },
    navbarTogglerPillNumber() {
      return this.friendInvitePillNumber; // more to be added if anything other than friend invites is added
    },
    friendInvitePillNumber() {
      return userStore.user?.friendInviteCount || 0;
    },
    showPoolsTab() {
      return !!userStore.user?.clubCount;
    },
    pendingClubActionCount() {
      const clubMembersToApproveCount = userStore.user?.clubMembersToApproveCount || 0;
      const oscarPoolsToLaunchCount = userStore.user?.oscarPoolsToLaunchCount || 0;
      return clubMembersToApproveCount + oscarPoolsToLaunchCount;
    },
    user() {
      return userStore.user;
    },
  },
  emits: ['log-out-user'],
  data() {
    return {
      searchType: "Titles",
      searchText: ''
    }
  },
  mounted() {
    // handle global keystroke events
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  },
  watch: {
    searchType(/*newValue, oldValue*/) {
      // if we're already on the Search Results page when the user updates the search type, immediately trigger a search.
      if ( this.$route.name === 'SearchResultsPage' ) {
          this.onSearch();
      }
    }
  },
  methods: {
    onKeyDown(_event) {
      if ( _event.ctrlKey && _event.altKey && ((_event.key === 'd') || (_event.key === 'D')) ) {
        _event.preventDefault(); // Prevents browser from opening "Add Bookmark" dialog

        this.toggleDarkMode();
      }
    },

    isActive(_page) {
      return this.$route.path.substring(1).startsWith(_page);
    },

    collapseOpenMenu() {
      // no navtoggle to collapse
      if (!this.$refs.navtoggle?.classList) {
        return;
      }
      // menu already collapsed
      if (this.$refs.navtoggle?.classList?.contains('collapsed')) {
        return;
      }
      // ok, collapse
      this.$refs.navtoggle.click();
    },

    async onSearch() {
      // console.log(`searching ${this.searchType}...`);
      this.searchText = this.searchText.trim().toLowerCase();
      if (this.searchText === '') {
        return;
      }
      this.collapseOpenMenu();

      // navigate to the results page and emit an event
      this.$router.push({name: 'SearchResultsPage', query: { searchType: this.searchType, searchText: this.searchText }});
      EventBus.emit('search-term-updated', this.searchType, this.searchText);
    },

    toggleDarkMode() {
      const htmlElement = document.documentElement;
      const currentTheme = htmlElement.getAttribute('data-bs-theme');
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      this.$colorTheme.setTheme(newTheme);
    },
},



}

</script>

<style>
* {
  --mol-navbar-content-height: 45px;
  --mol-navbar-top-padding: max(12px, env(safe-area-inset-top));
  --mol-navbar-bottom-padding: 12px;

  /*--mol-navbar-height: 70px;*/
  --mol-navbar-height: calc(var(--mol-navbar-content-height) + var(--mol-navbar-bottom-padding) + var(--mol-navbar-top-padding));
}
</style>

<style scoped>

  .btn-login {
    margin-right: 10px;
    margin-left: 10px;
  }

  .navbar {
    /*
    opacity: 50%;
     */
    border-bottom-style: solid;
    border-bottom-color: var(--bs-secondary);
    border-bottom-width: 1px;

    /*height: var(--mol-navbar-height);*/
    padding: var(--mol-navbar-top-padding) 0 var(--mol-navbar-bottom-padding) 0;
  }
  .navbar-nav {
    padding-left: 10px;
    padding-right: 10px;
  }
  .navbar-toggler {
    margin-right: 10px;
    z-index: 1;
  }
  .nav-item {
    .badge {
      font-size: 50%;
      vertical-align: top;
    }
    .active {
      font-weight: 700;
    }
  }
  .navbar-toggler-icon {
    .badge {
      font-size: 50%;
      vertical-align: top;
      margin-right: -45px;
      margin-top: -10px;
    }
  }

  .admin-colors {
    color: crimson!important;
  }

  .form-search {
    margin-left: 10px;
    margin-right: 10px;


    .search-button {
      input {
        border-right: none;
      }
      .btn-sm {
        font-size: smaller;
        color: var(--bs-body-color);
        border-color: var(--bs-body-color);
      }
      .dropdown-toggle-split {
        border-left: none;

      }
      margin-top: 3px;
      margin-bottom: 3px;
    }

    .form-control {
      border-color: var(--bs-btn-border-color);
      border-right: none;

      margin-top: 3px;
      margin-bottom: 3px;
      padding-top: 3px;
      padding-bottom: 3px;
    }
  }

  .navbar-brand {
    padding: 0px 0px 0px 0px;
    margin-right: 10px;
    margin-left: 10px;

    .logo-image {
      float: left;
    }

    .text-my {
      font-weight: 300;
    }
    .text-oscar {
      font-weight: 600;
    }
    .text-list {
      font-weight: 300;
    }
  }

  .user-dropdown {
    font-size: smaller;
    width: 170px;
    white-space: nowrap;
  }

</style>